import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import CoachCard from "@/components/CoachCard";
import HeroRotator from "@/components/HeroRotator";
import EditorialScroller from "@/components/EditorialScroller";
import SEO from "@/components/SEO";
import { buildWebPageLd } from "@/lib/seo/jsonld";
import { ShieldCheck, Award, BookMarked, Gauge, TrendingUp, ArrowRight, Plane, Anchor, Bus, Handshake } from "lucide-react";
import { collectionCard, ULTRA_LUXURY_CARD_CINEMATIC } from "@/lib/photos";

const trustProducts = [
  { icon: ShieldCheck, name: "Verified", tagline: "Seller identity and ownership independently confirmed.", trademark: "Vanturis Verified™" },
  { icon: Award, name: "Certified", tagline: "A standardized mechanical, systems, and cosmetic inspection.", trademark: "Vanturis Certified™" },
  { icon: BookMarked, name: "Passport", tagline: "A permanent digital record that transfers with the coach.", trademark: "Vanturis Passport™" },
  { icon: Gauge, name: "Score", tagline: "A transparent 0–100 condition and desirability rating.", trademark: "Vanturis Score™" },
  { icon: TrendingUp, name: "Value", tagline: "Analyst-assisted market range built from real comparables.", trademark: "Vanturis Value™" },
];

const converters = ["Marathon Coach", "Liberty Coach", "Millennium Luxury Coaches", "Featherlite Coaches", "Newell (Factory)", "Emerald Luxury Coaches"];
const coachTypes = [
  { key: "Luxury Motorhome", title: "Luxury Motorhomes", copy: "Two-couple layouts, gallery interiors, single-owner provenance." },
  { key: "Entertainer Coach", title: "Entertainer Coaches", copy: "Twelve-bunk tour buses ready for the road, complete with tour-provable service records." },
  { key: "Executive Coach", title: "Executive Coaches", copy: "Mobile boardrooms and hospitality suites for principal-level travel." },
  { key: "Motorsports Coach", title: "Motorsports Coaches", copy: "Team transporters with pit-lane spec bays and driver quarters." },
];

const webpSrcSet = (p) => {
  const b = p.replace(/\.(jpe?g)$/i, "");
  return `${b}.w480.webp 480w, ${b}.w960.webp 960w, ${b}.w1600.webp 1600w`;
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // P1F: defer the homepage featured fetch out of the critical LCP window.
    // The fetch → setFeatured → 6-card render is real main-thread work that
    // previously landed in the ~3s mobile burst. Running it at idle (or a
    // short fallback timer) keeps it off the main thread during first paint.
    let idleId = null, timerId = null;
    const load = () => {
      api.get("/listings/featured")
        .then(({ data }) => setFeatured(data))
        .catch(() => setFeatured([]))
        .finally(() => setLoading(false));
    };
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(load, { timeout: 1800 });
    } else {
      timerId = setTimeout(load, 400);
    }
    return () => {
      if (idleId && typeof window !== "undefined" && window.cancelIdleCallback) window.cancelIdleCallback(idleId);
      if (timerId) clearTimeout(timerId);
    };
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Vanturis | The Luxury Travel Marketplace"
        description="Vanturis is the luxury travel marketplace — born in luxury motorcoaches, Prevost conversions, entertainer coaches and premium motorhomes, and connected across private aviation and luxury marine through trusted specialists."
        path="/"
        exactTitle
        noIndex={false}
        ogTitle="Vanturis | Marketplace · Passport · Auctions"
        ogDescription="Vanturis is the luxury travel marketplace — built around exceptional motorcoaches and connected through trusted partners across private aviation and luxury marine."
        image="https://vanturis.com/social/vanturis-social-share-2026-luxury-travel.jpg?v=1"
        jsonLd={buildWebPageLd({
          path: "/",
          name: "Vanturis | The Luxury Travel Marketplace",
          description:
            "Vanturis is the luxury travel marketplace — born in luxury motorcoaches, Prevost conversions, entertainer coaches and premium motorhomes, and connected across private aviation and luxury marine through trusted specialists.",
        })}
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-bronze-dark text-ivory">
        <HeroRotator interval={6500} />
        <div className="relative container-editorial pt-24 md:pt-36 pb-32 md:pb-44 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8 lg:col-span-7 animate-fade-up">
            <p className="overline !text-copper-light mb-6" data-testid="hero-overline">The Luxury Travel Marketplace</p>
            <h1 className="font-serif font-light text-[42px] sm:text-6xl lg:text-[86px] leading-[0.95] tracking-tight text-ivory">
              A curated marketplace for<br />
              <em className="text-copper-light">exceptional coaches</em>.
            </h1>
            <p className="mt-7 font-serif italic text-xl md:text-2xl text-copper-light/90 leading-snug" data-testid="hero-brand-story">
              Born in luxury coaches. Connected across luxury travel.
            </p>
            <p className="mt-6 text-lg md:text-xl font-light leading-relaxed text-ivory/85 max-w-2xl">
              Discover professionally presented Prevost conversions, entertainer coaches, Newell
              motorcoaches and executive coaches from verified sellers &mdash; now connected to a
              trusted network across private aviation and luxury marine.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/browse" className="btn-primary bg-copper hover:bg-copper-light" data-testid="hero-browse">
                Browse Coaches <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/list-coach" className="btn-secondary border-ivory text-ivory hover:bg-ivory hover:text-bronze" data-testid="hero-list">
                List Your Coach
              </Link>
            </div>
          </div>
          <div className="md:col-span-4 lg:col-span-5 hidden md:block animate-fade-up" style={{ animationDelay: "200ms" }}>
            <div className="border border-ivory/25 bg-ivory/5 backdrop-blur-md p-8 space-y-6">
              <p className="overline !text-copper-light">Confidence in every coach</p>
              <div className="space-y-4 text-sm leading-relaxed text-ivory/90">
                <p className="font-serif italic text-xl leading-snug">&ldquo;The trust standard for the luxury motorcoach-conversion market.&rdquo;</p>
                {/* Non-numeric trust statements — no fabricated scale, inventory,
                    verification, or converter counts.  These claims are true
                    at launch regardless of catalogue size. */}
                <ul className="pt-4 border-t border-ivory/20 space-y-3 text-left" data-testid="hero-trust-statements">
                  <li className="flex items-start gap-3">
                    <span className="text-copper-light mt-1">◆</span>
                    <span>Curated specialty-vehicle marketplace</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-copper-light mt-1">◆</span>
                    <span>Seller-provided details presented clearly</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-copper-light mt-1">◆</span>
                    <span>Public-safe listing information</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-copper-light mt-1">◆</span>
                    <span>Guided buyer and seller support</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED LISTINGS */}
      <section className="container-editorial py-24 md:py-32">
        <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
          <div className="max-w-xl">
            <p className="overline mb-4">Featured Listings</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-tight text-ink">
              Recently listed <em className="text-copper">exceptional coaches</em>.
            </h2>
          </div>
          <Link to="/browse" className="btn-ghost" data-testid="featured-view-all">
            View all coaches <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[0,1,2,3,4,5].map((i) => (
              <div key={i} className="aspect-[4/3] bg-ivory-300 animate-pulse" />
            ))}
          </div>
        ) : featured.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="featured-grid">
            {featured.map((l, i) => (
              <CoachCard key={l.id} listing={l} priority={false} deferSourceUntilVisible />
            ))}
          </div>
        ) : (
          <div
            className="border border-ivory-400 bg-gradient-to-br from-ivory-100 via-ivory to-ivory-100 px-8 py-16 md:py-24 text-center relative overflow-hidden"
            data-testid="featured-empty-state"
          >
            {/* Corner editorial hairlines to match the OG card motif */}
            <span className="absolute top-6 left-6 w-8 h-8 border-t border-l border-copper/50"></span>
            <span className="absolute top-6 right-6 w-8 h-8 border-t border-r border-copper/50"></span>
            <span className="absolute bottom-6 left-6 w-8 h-8 border-b border-l border-copper/50"></span>
            <span className="absolute bottom-6 right-6 w-8 h-8 border-b border-r border-copper/50"></span>

            <p className="text-[11px] uppercase tracking-[0.32em] text-copper mb-4">
              Featured Placement · Available
            </p>
            <h3 className="font-serif font-light text-3xl md:text-4xl text-ink leading-[1.15] max-w-2xl mx-auto mb-4">
              No featured coaches yet — <em className="text-copper">be the first</em>.
            </h3>
            <p className="text-ink-100 text-base max-w-xl mx-auto mb-8 leading-relaxed">
              This is the highest-visibility placement on Vanturis. Elevate a
              single listing to the homepage and put your coach in front of
              every serious buyer.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/pricing"
                className="btn-primary"
                data-testid="featured-empty-cta"
              >
                Feature Your Listing <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/browse"
                className="btn-ghost"
                data-testid="featured-empty-browse"
              >
                Browse all coaches
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* BROWSE BY TYPE */}
      <EditorialScroller />

      <section className="bg-ivory-200 py-24 md:py-32">
        <div className="container-editorial">
          <div className="max-w-3xl mb-14">
            <p className="overline mb-4">Browse by Coach Type</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight text-ink">
              Every coach we carry has been professionally converted &mdash; nothing else here.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coachTypes.map((t) => (
              <Link
                key={t.key}
                to={`/browse?coach_type=${encodeURIComponent(t.key)}`}
                className="group block bg-ivory border border-ivory-400 hover:border-bronze p-8 transition-colors"
                data-testid={`type-${t.key.toLowerCase().replace(/\s+/g,'-')}`}
              >
                <div className="text-copper text-xs uppercase tracking-[0.24em] mb-4">{t.key}</div>
                <h3 className="font-serif text-2xl text-ink leading-tight mb-4">{t.title}</h3>
                <p className="text-sm text-ink-100 leading-relaxed">{t.copy}</p>
                <div className="mt-6 text-xs uppercase tracking-[0.22em] text-bronze group-hover:text-copper transition-colors flex items-center gap-2">
                  Explore <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BORN IN COACHES — brand story */}
      <section className="bg-bronze-dark text-ivory py-24 md:py-32 relative overflow-hidden" data-testid="home-born-in-coaches">
        <div className="container-editorial relative grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <p className="overline !text-copper-light mb-5">Born on the road. Built beyond it.</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight text-ivory">
              Vanturis was created around the luxury motorcoach market.
            </h2>
            <p className="mt-7 text-ivory/80 leading-relaxed text-lg font-light max-w-2xl">
              A relationship-driven world of complex assets, fragmented history and high-value
              transactions &mdash; the segment Vanturis knows most deeply. That foundation now
              connects buyers, sellers and trusted specialists across private aviation and luxury marine.
            </p>
            <p className="mt-6 text-copper-light text-sm uppercase tracking-[0.24em]">
              Marketplace · Passport · Auctions
            </p>
          </div>
          <div className="md:col-span-5 md:justify-self-end">
            <div className="border border-ivory/25 bg-ivory/5 backdrop-blur-md p-8 space-y-5 max-w-sm">
              {[
                { icon: Bus, label: "Land", note: "Our foundation — Vanturis specialty" },
                { icon: Plane, label: "Air", note: "Private aviation — trusted partners" },
                { icon: Anchor, label: "Water", note: "Luxury marine — trusted partners" },
              ].map((e) => {
                const Icon = e.icon;
                return (
                  <div key={e.label} className="flex items-start gap-4">
                    <Icon className="w-5 h-5 text-copper-light mt-0.5" strokeWidth={1.5} />
                    <div>
                      <div className="font-serif text-lg text-ivory leading-none">{e.label}</div>
                      <div className="text-xs text-ivory/60 mt-1 tracking-wide">{e.note}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* LUXURY TRAVEL — LAND · AIR · WATER (Land dominant) */}
      <section className="container-editorial py-24 md:py-32" data-testid="home-luxury-travel">
        <div className="max-w-3xl mb-14">
          <p className="overline mb-4">Land · Air · Water</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight text-ink">
            One ecosystem. Built on the road, connected beyond it.
          </h2>
          <p className="text-sm text-ink-100 mt-6 leading-relaxed max-w-xl">
            Land is our core operating expertise. Air and Water expand the Vanturis network through
            trusted specialists and partners &mdash; the same editorial discipline, extended into the
            air and onto the water.
          </p>
        </div>

        {/* LAND — dominant banner */}
        <Link
          to="/browse"
          className="group relative block overflow-hidden border border-ivory-400 hover:border-bronze transition-colors"
          data-testid="home-vertical-land"
        >
          <div className="aspect-[4/3] sm:aspect-[21/9] lg:aspect-[21/7] min-h-[320px] sm:min-h-0 overflow-hidden">
            <img
              src={ULTRA_LUXURY_CARD_CINEMATIC}
              srcSet={webpSrcSet(ULTRA_LUXURY_CARD_CINEMATIC)}
              sizes="(min-width: 1024px) 100vw, 100vw"
              alt="Luxury motorcoach against a dramatic sky on Vanturis"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 text-ivory">
            <div className="text-copper-light text-[10px] uppercase tracking-[0.24em] mb-2 flex items-center gap-2">
              <Bus className="w-4 h-4" strokeWidth={1.5} /> Land — Our Foundation · Vanturis Specialty
            </div>
            <h3 className="font-serif text-2xl md:text-3xl lg:text-5xl leading-tight max-w-2xl">
              Luxury motorcoaches, entertainer coaches &amp; premium motorhomes.
            </h3>
            <p className="text-sm md:text-base text-ivory/80 mt-3 max-w-xl leading-relaxed">
              Prevost conversions, Class A &amp; Super C, overland and specialty travel assets.
            </p>
            <div className="mt-5 text-xs uppercase tracking-[0.22em] text-copper-light flex items-center gap-2">
              Browse Coaches <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </Link>

        {/* AIR + WATER — trusted partner extensions */}
        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {[
            { slug: "private-aviation", to: "/marketplace/private-aviation", icon: Plane,
              overline: "Air — Trusted Partners", title: "Private Aviation",
              copy: "Private jets and business aircraft through a curated network of aviation specialists — qualified introductions, partner listings and auction opportunities." },
            { slug: "luxury-marine", to: "/marketplace/luxury-marine", icon: Anchor,
              overline: "Water — Trusted Partners", title: "Luxury Marine",
              copy: "Motor yachts, superyachts and premium vessels through trusted marine specialists — qualified introductions, partner inventory and auction opportunities." },
          ].map((c) => (
            <Link key={c.slug} to={c.to}
              className="group relative overflow-hidden border border-ivory-400 hover:border-bronze transition-colors"
              data-testid={`home-vertical-${c.slug}`}>
              <div className="aspect-[16/10] overflow-hidden">
                <img src={collectionCard(c.slug)} alt={c.title}
                     srcSet={webpSrcSet(collectionCard(c.slug))}
                     sizes="(min-width: 768px) 50vw, 100vw"
                     loading="lazy" decoding="async"
                     className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/25 to-transparent" />
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-7 text-ivory">
                <div className="text-copper-light text-[10px] uppercase tracking-[0.24em] mb-2 flex items-center gap-2">
                  <c.icon className="w-4 h-4" strokeWidth={1.5} /> {c.overline}
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight">{c.title}</h3>
                <p className="text-sm text-ivory/75 mt-2 max-w-md leading-relaxed">{c.copy}</p>
                <div className="mt-4 text-xs uppercase tracking-[0.22em] text-copper-light flex items-center gap-2">
                  Explore <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
        <p className="mt-6 text-xs text-ink-100 leading-relaxed max-w-3xl flex items-start gap-2" data-testid="home-partner-disclosure">
          <Handshake className="w-4 h-4 text-copper flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <span>
            Vanturis is a marketplace and technology platform. Private Aviation and Luxury Marine
            opportunities are presented through trusted specialist partners; Vanturis does not broker,
            operate or independently certify aircraft or vessels.
          </span>
        </p>
      </section>

      {/* BROWSE BY CONVERTER */}
      <section className="container-editorial py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <p className="overline mb-4">By Converter</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight text-ink">
              A curated list of the industry&rsquo;s most respected converters.
            </h2>
            <p className="text-sm text-ink-100 mt-6 leading-relaxed max-w-sm">
              Marathon, Liberty, Millennium, Featherlite, Newell, and the small handful of shops whose
              work sets the standard for the segment.
            </p>
          </div>
          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-3 gap-px bg-ivory-400 border border-ivory-400">
            {converters.map((c) => (
              <Link
                key={c}
                to={`/browse?converter=${encodeURIComponent(c)}`}
                className="bg-ivory hover:bg-ivory-200 transition-colors p-8 group"
                data-testid={`converter-${c.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}
              >
                <div className="font-serif text-2xl text-ink group-hover:text-copper transition-colors">{c.split(" ")[0]}</div>
                <div className="text-xs text-ink-100 mt-2 uppercase tracking-[0.2em]">{c}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST PRODUCTS */}
      <section className="bg-bronze-dark text-ivory py-24 md:py-32 relative overflow-hidden">
        <div className="container-editorial relative">
          <div className="max-w-2xl mb-16">
            <p className="overline !text-copper-light mb-4">The Vanturis Trust Standard</p>
            <h2 className="font-serif font-light text-4xl md:text-6xl leading-[1.05] tracking-tight">
              Trust is the product. <em className="text-copper-light">Everything else is a coach.</em>
            </h2>
            <p className="mt-6 text-ivory/80 leading-relaxed text-lg font-light max-w-xl">
              Every meaningful moment in a coach&rsquo;s life &mdash; verification, inspection, service,
              ownership &mdash; belongs on one connected record.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-ivory/10 border border-ivory/10">
            {trustProducts.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.name} className="bg-bronze-dark hover:bg-bronze p-8 md:p-10 transition-colors">
                  <Icon className="w-6 h-6 text-copper-light mb-6" strokeWidth={1.25} />
                  <div className="text-[10px] uppercase tracking-[0.28em] text-copper-light mb-3">{p.trademark}</div>
                  <h3 className="font-serif text-2xl mb-4">Vanturis {p.name}</h3>
                  <p className="text-sm text-ivory/70 leading-relaxed">{p.tagline}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12">
            <Link to="/trust" className="btn-primary bg-copper hover:bg-copper-light" data-testid="trust-learn">
              Explore Trust Products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BUYERS & SELLERS SPLIT */}
      <section className="container-editorial py-24 md:py-32 grid md:grid-cols-2 gap-16 md:gap-24">
        <div>
          <p className="overline mb-4">For Buyers</p>
          <h3 className="font-serif font-light text-3xl md:text-5xl leading-[1.1] text-ink mb-6">
            Every coach you consider,<br />on a single connected record.
          </h3>
          <ul className="space-y-4 mt-8">
            {[
              "Curated sellers &mdash; onboarded through the Vanturis concierge process.",
              "Standardized inspections that read the same across every coach.",
              "Side-by-side comparisons across converters, chassis, and floorplans.",
              "Introductions to trusted financing, insurance, and transportation.",
              "Secure, in-platform messaging with an audit trail.",
            ].map((item, i) => (
              <li key={i} className="flex gap-4 text-ink-50">
                <span className="mt-1 w-6 h-px bg-copper flex-shrink-0" />
                <span dangerouslySetInnerHTML={{ __html: item }} />
              </li>
            ))}
          </ul>
          <Link to="/browse" className="btn-secondary mt-10 inline-flex" data-testid="cta-buyer-browse">
            Start Browsing
          </Link>
        </div>
        <div>
          <p className="overline mb-4">For Sellers</p>
          <h3 className="font-serif font-light text-3xl md:text-5xl leading-[1.1] text-ink mb-6">
            Reach the buyers who take<br />your coach seriously.
          </h3>
          <ul className="space-y-4 mt-8">
            {[
              "Free standard listings for private owners and dealers.",
              "Premium editorial presentation packages when it matters.",
              "Analyst-assisted valuation with real comparable data.",
              "Professional photography, video, and drone coordination.",
              "Concierge support from listing through closing.",
            ].map((item, i) => (
              <li key={i} className="flex gap-4 text-ink-50">
                <span className="mt-1 w-6 h-px bg-copper flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <Link to="/list-coach" className="btn-primary mt-10 inline-flex" data-testid="cta-seller-list">
            List Your Coach
          </Link>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="container-editorial pb-24 md:pb-32">
        <div className="border border-ivory-400 bg-ivory-100 grid md:grid-cols-2 gap-8 items-center p-10 md:p-20">
          <div>
            <p className="overline mb-4">Vanturis Concierge</p>
            <h3 className="font-serif font-light text-3xl md:text-5xl leading-tight text-ink">
              Talk with a specialist.
            </h3>
            <p className="text-ink-100 mt-6 leading-relaxed max-w-md">
              A member of the Vanturis team can help you shortlist coaches, coordinate an inspection,
              and manage the transaction end-to-end.
            </p>
          </div>
          <div className="flex flex-col md:items-end gap-4">
            <Link to="/register" className="btn-primary" data-testid="cta-concierge">Speak With Concierge</Link>
            <Link to="/browse" className="btn-ghost">Or browse listings →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
